import React, { useState, useRef, useEffect } from 'react';
import { Table, Button, Modal, Form, Card } from 'react-bootstrap';
import AddNewButton from '../../../Common/AddNewButton';
import Select from 'react-select';
import { addTeam, editTeam, deleteTeam, getTeamById, fetchTeamsFromPreviousSeason, mapTeamsFromPreviousSeason, listTeams } from '../../../api/tournament/teamService';
import { DeleteModal } from '../../../Common/DeleteModal';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import LogoUpload from '../../../components/LogoUpload/LogoUpload';
import { getImageUrl } from '../../../utils/imageUrl';
import noResultFoundIcon from '../../../assets/images/admin-icons/no-result-found.png';
import { toast } from 'react-toastify';
import TournamentBulkUploadModal from '../../../components/tournament/TournamentBulkUploadModal';
import { getTeamId } from './teamTableData';

interface TeamsTableProps {
  teams: any[];
  search: string;
  onSearch: (value: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onAdd: () => void;
  ageCategories: any[];
  tournamentId: string;
  teamsLoading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  ageCategoryFilter?: any;
  onAgeCategoryFilterChange?: (filter: any) => void;
  tournamentSeasonId?: string; // Optional: tournamentSeason id for season context
  seasonNumber?: number; // Optional: season number to determine if "Add Existing Team" should be shown
  tournamentSports?: string; // Add sport prop for API calls
}

const genderOptions = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

const countryCodeOptions = [
  { value: '+91', label: '+91' },
  { value: '+1', label: '+1' },
  { value: '+44', label: '+44' },
  // Add more as needed
];

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const TeamsTable: React.FC<TeamsTableProps> = ({ teams, search, onSearch, onEdit, onDelete, onAdd, ageCategories, tournamentId, teamsLoading, page, pageSize, totalPages, totalCount, onPageChange, onPageSizeChange, ageCategoryFilter, onAgeCategoryFilterChange, tournamentSeasonId, seasonNumber, tournamentSports }) => {
  // Helper to get sport - use prop if provided, otherwise throw error
  const getSport = (): string => {
    if (tournamentSports) {
      return tournamentSports;
    }
    throw new Error('Tournament sport is required. Please ensure tournamentSports prop is passed to TeamsTable.');
  };
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<any>({
    name: '',
    shortName: '',
    emailId: '',
    managerName: '',
    logo: '',
    countryCode: countryCodeOptions[0],
    managerContactNum: '',
    ageCategory: [],
    gender: genderOptions[0],
    tournament: tournamentId,
  });
  const [formTouched, setFormTouched] = useState<any>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [loadingTeamDetails, setLoadingTeamDetails] = useState(false);
  const [teamSeasonInfoId, setTeamSeasonInfoId] = useState<string | null>(null); // Store teamSeasonInfo._id for edits
  
  // State for "Add Existing Team" modal
  const [showExistingTeamModal, setShowExistingTeamModal] = useState(false);
  const [selectedAgeCategory, setSelectedAgeCategory] = useState<any>(null);
  const [previousSeasonTeams, setPreviousSeasonTeams] = useState<any[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [loadingPreviousTeams, setLoadingPreviousTeams] = useState(false);
  const [mappingTeams, setMappingTeams] = useState(false);
  const [existingTeamError, setExistingTeamError] = useState<string | null>(null);
  const [alreadyAddedTeamIds, setAlreadyAddedTeamIds] = useState<Set<string>>(new Set());
  const [loadingCurrentTeams, setLoadingCurrentTeams] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  const ageCategoryOptions = ageCategories.map((ac: any) => ({ value: ac.ageCategory || ac._id, label: ac.ageCategory }));


  const filteredTeams = !ageCategoryFilter || ageCategoryFilter.length === 0
    ? teams
    : teams.filter(team => {
        const teamAgeCategories = team.teamSeasonInfo?.ageCategory || team.ageCategory || [];
        const ageCatArray = Array.isArray(teamAgeCategories) ? teamAgeCategories : [teamAgeCategories];
        
        return ageCatArray.some((cat: string) => 
          ageCategoryFilter.some((f: any) => f.value === cat)
        );
      });

  const validateForm = () => {
    return {
      name: !form.name.trim() ? 'Name is required.' : '',
      shortName: !form.shortName.trim() ? 'Abbreviation is required.' : '',
      ageCategory: !form.ageCategory || form.ageCategory.length === 0 ? 'At least one Age Category is required.' : '',
      tournament: !tournamentId ? 'Tournament is required.' : '',
    };
  };
  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };
  const handleSelectChange = (name: string, option: any) => {
    setForm({ ...form, [name]: option });
    setFormTouched({ ...formTouched, [name]: true });
  };
  const handleAgeCategoryChange = (options: any) => {
    setForm({ ...form, ageCategory: options });
    setFormTouched({ ...formTouched, ageCategory: true });
  };

  const handleAdd = () => {
    setEditIndex(null);
    setViewMode(false);
    setTeamSeasonInfoId(null);
    setForm({
      name: '',
      shortName: '',
      emailId: '',
      managerName: '',
      logo: '',
      countryCode: countryCodeOptions[0],
      managerContactNum: '',
      ageCategory: [],
      gender: genderOptions[0],
      tournament: tournamentId,
    });
    setFormTouched({});
    setFormError(null);
    setSaving(false);
    setLogoError(null);
    setShowAddForm(true);
    setTimeout(() => {
      if (cardRef.current) {
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  const handleEdit = async (idx: number) => {
    setEditIndex(idx);
    setViewMode(false);
    const t = teams[idx];
    const teamId = t.team?._id || t._id || t.id;
    const teamSeasonInfoId = t._id || t.teamSeasonInfo?._id;
    
    setLoadingTeamDetails(true);
    setFormError(null);
    
    try {
      // Get sport from prop or default to 'Football'
      const sport = getSport();
      // FIX: Pass sport parameter to getTeamById
      const teamDetails = await getTeamById(teamId, teamSeasonInfoId, sport);
      const teamData = teamDetails.data || teamDetails;
      
      setForm({
        name: teamData.name || '',
        shortName: teamData.shortName || '',
        emailId: teamData.emailId || '',
        managerName: teamData.managerName || '',
        logo: teamData.logo || '',
        countryCode: countryCodeOptions.find(opt => opt.value === teamData.countryCode) || countryCodeOptions[0],
        managerContactNum: teamData.managerContactNum || '',
        ageCategory: ((teamData.teamSeasonInfo?.ageCategory || teamData.ageCategory) || []).map((ac: string) => ageCategoryOptions.find(opt => opt.value === ac) || { value: ac, label: ac }),
        gender: genderOptions.find(opt => opt.value === teamData.gender) || genderOptions[0],
        tournament: tournamentId,
      });
      setTeamSeasonInfoId(teamData.teamSeasonInfo?._id || null);
      setFormTouched({});
      setSaving(false);
      setLogoError(null);
      setShowAddForm(true);
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching team details:', error);
      setFormError('Failed to load team details.');
    } finally {
      setLoadingTeamDetails(false);
    }
  };

  const handleView = async (idx: number) => {
    setEditIndex(idx);
    setViewMode(true);
    const t = teams[idx];
    const teamId = t.team?._id || t._id || t.id;
    const teamSeasonInfoId = t._id || t.teamSeasonInfo?._id;
    
    setLoadingTeamDetails(true);
    setFormError(null);
    
    try {
      // Get sport from prop or default to 'Football'
      const sport = getSport();
      // FIX: Pass sport parameter to getTeamById
      const teamDetails = await getTeamById(teamId, teamSeasonInfoId, sport);
      const teamData = teamDetails.data || teamDetails;
      
      setForm({
        name: teamData.name || '',
        shortName: teamData.shortName || '',
        emailId: teamData.emailId || '',
        managerName: teamData.managerName || '',
        logo: teamData.logo || '',
        countryCode: countryCodeOptions.find(opt => opt.value === teamData.countryCode) || countryCodeOptions[0],
        managerContactNum: teamData.managerContactNum || '',
        ageCategory: ((teamData.teamSeasonInfo?.ageCategory || teamData.ageCategory) || []).map((ac: string) => ageCategoryOptions.find(opt => opt.value === ac) || { value: ac, label: ac }),
        gender: genderOptions.find(opt => opt.value === teamData.gender) || genderOptions[0],
        tournament: tournamentId,
      });
      setTeamSeasonInfoId(teamData.teamSeasonInfo?._id || null);
      setFormTouched({});
      setSaving(false);
      setLogoError(null);
      setShowAddForm(true);
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching team details:', error);
      setFormError('Failed to load team details.');
    } finally {
      setLoadingTeamDetails(false);
    }
  };
  const handleClose = () => {
    setShowAddForm(false);
    setTeamSeasonInfoId(null);
  };

  const handleSave = async () => {
    setFormTouched({
      name: true,
      ageCategory: true,
      tournament: true,
      shortName: true,
      emailId: true,
      managerName: true,
      countryCode: true,
      managerContactNum: true,
      gender: true,
    });
    if (!isFormValid) return;
    setFormError(null);
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        shortName: form.shortName,
        emailId: form.emailId,
        managerName: form.managerName,
        logo: form.logo,
        countryCode: form.countryCode.value,
        managerContactNum: form.managerContactNum,
        ageCategory: form.ageCategory.map((ac: any) => ac.value),
        gender: form.gender.value,
      };
      
      payload.tournament = tournamentId;
      
      if (tournamentSeasonId) {
        payload.tournamentSeason = tournamentSeasonId;
      }
      
      // Get sport from prop or default to 'Football'
      const sport = getSport();
      
      if (editIndex === null) {
        await addTeam(payload, sport); // FIX: Pass sport parameter
        onAdd();
      } else {
        const team = teams[editIndex];
        const teamId = team.team?._id || team._id || team.id;
        if (teamSeasonInfoId) {
          payload.teamSeasonInfo = {
            _id: teamSeasonInfoId
          };
        }
        await editTeam(teamId, payload, sport); // FIX: Pass sport parameter
        onEdit();
      }
      setShowAddForm(false);
    } catch (err: any) {
      console.error('Error saving team:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      
      // Extract detailed error message from API response
      let errorMessage = 'Failed to save team. Please try again.';
      
      if (err.response) {
        // API responded with error status
        const responseData = err.response.data;
        
        if (responseData?.error) {
          // Use the specific error message from API (e.g., "Team already exists in this tournament season")
          errorMessage = responseData.error;
        } else if (responseData?.message) {
          // Alternative error field
          errorMessage = responseData.message;
        } else if (typeof responseData === 'string') {
          // Plain text error response
          errorMessage = responseData;
        } else if (responseData?.errors && Array.isArray(responseData.errors)) {
          // Multiple validation errors
          errorMessage = responseData.errors.join(', ');
        } else if (responseData?.details) {
          // Error with details
          errorMessage = responseData.details;
        } else if (responseData?.title) {
          // Error title field
          errorMessage = responseData.title;
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection and try again.';
      } else if (err.message) {
        // Other error
        errorMessage = err.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (idx: number) => {
    setDeleteIndex(idx);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (deleteIndex !== null) {
      const team = teams[deleteIndex];
      // For delete, use teamSeason ID (team._id) not team ID (team.team._id)
      // The delete API endpoint expects the teamSeason ID
      const teamSeasonId = team._id;
      if (!teamSeasonId) {
        setFormError('Team season ID not found. Cannot delete team.');
        return;
      }
      try {
        // Get sport from prop or default to 'Football'
        const sport = getSport();
        await deleteTeam(teamSeasonId, sport); // FIX: Use teamSeason ID for delete
        setShowDeleteModal(false);
        setDeleteIndex(null);
        onDelete();
      } catch (err) {
        setFormError('Failed to delete team.');
      }
    }
  };
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteIndex(null);
  };

  const handleOpenExistingTeamModal = () => {
    setShowExistingTeamModal(true);
    setSelectedAgeCategory(null);
    setPreviousSeasonTeams([]);
    setSelectedTeamIds(new Set());
    setTeamSearchTerm('');
    setExistingTeamError(null);
  };

  const handleCloseExistingTeamModal = () => {
    setShowExistingTeamModal(false);
    setSelectedAgeCategory(null);
    setPreviousSeasonTeams([]);
    setSelectedTeamIds(new Set());
    setTeamSearchTerm('');
    setExistingTeamError(null);
    setAlreadyAddedTeamIds(new Set());
  };

  const handleExistingTeamAgeCategoryChange = async (selectedOption: any) => {
    setSelectedAgeCategory(selectedOption);
    setPreviousSeasonTeams([]);
    setSelectedTeamIds(new Set());
    setExistingTeamError(null);
    setAlreadyAddedTeamIds(new Set());

    if (!selectedOption || !tournamentId || !tournamentSeasonId) {
      return;
    }

    try {
      setLoadingPreviousTeams(true);
      setLoadingCurrentTeams(true);
      
      // Get sport from prop or default to 'Football'
      const sport = getSport();
      const teams = await fetchTeamsFromPreviousSeason(
        tournamentId,
        tournamentSeasonId,
        selectedOption.value,
        sport // FIX: Pass sport parameter
      );
      setPreviousSeasonTeams(teams || []);
      
      try {
        // Get sport from prop or default to 'Football'
        const sport = getSport();
        const currentTeamsResponse = await listTeams({
          tournamentId: tournamentId,
          tournamentSeason: tournamentSeasonId,
          ageCategory: selectedOption.value,
          page: 1,
          pageSize: 1000,
          sport: sport // FIX: Pass sport parameter
        });
        
        const currentTeams = currentTeamsResponse?.paginatedResults || [];
        const currentTeamIds = new Set<string>();
        currentTeams.forEach((team: any) => {
          const teamId = team.team?._id || team._id;
          if (teamId) {
            currentTeamIds.add(teamId);
          }
        });
        
        setAlreadyAddedTeamIds(currentTeamIds);
      } catch (currentTeamsError: any) {
        console.error('Error fetching current teams:', currentTeamsError);
        setAlreadyAddedTeamIds(new Set());
      }
    } catch (error: any) {
      console.error('Error fetching teams from previous season:', error);
      setExistingTeamError(error.message || 'Failed to fetch teams from previous season');
      setPreviousSeasonTeams([]);
    } finally {
      setLoadingPreviousTeams(false);
      setLoadingCurrentTeams(false);
    }
  };

  const handleTeamToggle = (teamId: string) => {
    if (alreadyAddedTeamIds.has(teamId)) {
      return;
    }
    
    setSelectedTeamIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
      }
      return newSet;
    });
  };

  const handleSelectAllTeams = () => {
    const filteredTeams = getFilteredPreviousSeasonTeams();
    const selectableTeams = filteredTeams.filter((team: any) => {
      const teamId = team.team?._id || team._id;
      return !alreadyAddedTeamIds.has(teamId);
    });
    
    const allSelectableSelected = selectableTeams.length > 0 && 
      selectableTeams.every((team: any) => {
        const teamId = team.team?._id || team._id;
        return selectedTeamIds.has(teamId);
      });
    
    if (allSelectableSelected) {
      setSelectedTeamIds(prev => {
        const newSet = new Set(prev);
        selectableTeams.forEach((team: any) => {
          const teamId = team.team?._id || team._id;
          newSet.delete(teamId);
        });
        return newSet;
      });
    } else {
      setSelectedTeamIds(prev => {
        const newSet = new Set(prev);
        selectableTeams.forEach((team: any) => {
          const teamId = team.team?._id || team._id;
          newSet.add(teamId);
        });
        return newSet;
      });
    }
  };

  const getFilteredPreviousSeasonTeams = () => {
    if (!teamSearchTerm.trim()) {
      return previousSeasonTeams;
    }
    const searchLower = teamSearchTerm.toLowerCase();
    return previousSeasonTeams.filter((team: any) => {
      const teamData = team.team || team;
      const name = (teamData.name || '').toLowerCase();
      const shortName = (teamData.shortName || '').toLowerCase();
      return name.includes(searchLower) || shortName.includes(searchLower);
    });
  };

  const handleMapTeams = async () => {
    if (!selectedAgeCategory) {
      setExistingTeamError('Please select an age category');
      return;
    }

    const selectableTeamIds = Array.from(selectedTeamIds).filter(teamId => !alreadyAddedTeamIds.has(teamId));

    if (selectableTeamIds.length === 0) {
      setExistingTeamError('Please select at least one team that is not already added');
      return;
    }

    if (!tournamentSeasonId) {
      setExistingTeamError('Season ID is missing');
      return;
    }

    try {
      setMappingTeams(true);
      setExistingTeamError(null);
      
      // Get sport from prop or default to 'Football'
      const sport = getSport();
      await mapTeamsFromPreviousSeason(tournamentSeasonId, {
        previousSeasonTeams: selectableTeamIds,
        ageCategory: selectedAgeCategory.value
      }, sport); // FIX: Pass sport parameter

      const teamCount = selectableTeamIds.length;
      const successMessage = teamCount === 1 
        ? 'Team added successfully!' 
        : `${teamCount} teams added successfully!`;
      
      toast.success(successMessage, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });

      onAdd();
      handleCloseExistingTeamModal();
    } catch (error: any) {
      console.error('Error mapping teams from previous season:', error);
      setExistingTeamError(error.response?.data?.error || error.message || 'Failed to add teams from previous season');
    } finally {
      setMappingTeams(false);
    }
  };

  const [inputValue, setInputValue] = useState(search);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (search === '' && inputValue !== '') {
      setInputValue('');
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      }, 0);
    }
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue.length === 0 || inputValue.length >= 2) {
        onSearch(inputValue);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue, onSearch]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Teams</h4>
          <div className="d-flex align-items-center gap-3">
            <div style={{ minWidth: 200, position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search teams..."
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                ref={inputRef}
                style={{ 
                  minHeight: '38px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  paddingRight: inputValue ? '35px' : '12px'
                }}
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={() => { setInputValue(''); onSearch(''); }}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#6c757d',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '0',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <div style={{ minWidth: 200 }}>
              <Select
                classNamePrefix="react-select"
                name="ageCategoryFilter"
                value={ageCategoryFilter}
                onChange={onAgeCategoryFilterChange}
                options={[{ value: '', label: 'All Age Categories' }, ...ageCategoryOptions]}
                placeholder="Age Category"
                isClearable
                isSearchable
                isMulti
                styles={{ 
                  control: base => ({ 
                    ...base, 
                    minHeight: '38px', 
                    borderRadius: '6px',
                    fontSize: '14px'
                  })
                }}
              />
            </div>
            {seasonNumber && seasonNumber > 1 && (
              <Button 
                variant="outline-primary" 
                onClick={handleOpenExistingTeamModal}
                style={{ 
                  borderRadius: '6px', 
                  marginRight: '8px',
                  backgroundColor: '#ffffff',
                  borderColor: '#0d6efd',
                  color: '#0d6efd',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Add Existing Team <span style={{ marginLeft: '4px' }}>+</span>
              </Button>
            )}
            {tournamentSeasonId && (
              <Button
                variant="outline-primary"
                onClick={() => setShowBulkUploadModal(true)}
                style={{ borderRadius: '6px', marginRight: '8px' }}
              >
                <i className="ri-upload-2-line me-1" />
                Bulk Upload
              </Button>
            )}
            <Button 
              variant="primary" 
              onClick={handleAdd}
              style={{ 
                borderRadius: '6px',
                backgroundColor: '#0d6efd',
                borderColor: '#0d6efd',
                color: '#ffffff',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Add New <span style={{ marginLeft: '4px' }}>+</span>
            </Button>
          </div>
        </div>
      
      {showAddForm && (
        <Card ref={cardRef} className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {editIndex === null ? 'Add Team' : viewMode ? 'View Team' : 'Edit Team'}
            </h5>
            <Button variant="outline-secondary" size="sm" onClick={handleClose}>
              <i className="ri-close-line"></i>
            </Button>
          </Card.Header>
          <Card.Body>
            {formError && <div className="alert alert-danger">{formError}</div>}
            <Form>
              <div className="row mb-3">
                <div className="col-md-6">
                  <LogoUpload
                    currentLogo={form.logo}
                    onLogoChange={(logoUrl: string) => {
                      setForm({ ...form, logo: logoUrl });
                      setFormTouched({ ...formTouched, logo: true });
                    }}
                    isEditMode={!viewMode}
                    context="teams/logo"
                    maxSizeMB={1}
                    maxWidthOrHeight={800}
                    size={80}
                    title="Team Logo"
                    label="Team Logo"
                    compact={true}
                  />
                  {logoError && <div className="text-danger mt-2">{logoError}</div>}
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-medium">
                      Age Category<span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      name="ageCategory"
                      value={form.ageCategory}
                      onChange={handleAgeCategoryChange}
                      options={ageCategoryOptions}
                      placeholder="Select Age Category"
                      isMulti
                      isClearable={false}
                      isDisabled={viewMode}
                      menuPortalTarget={document.body}
                      styles={{ 
                        control: base => ({ ...base, minHeight: '38px' }),
                        menuPortal: base => ({ ...base, zIndex: 10000 })
                      }}
                    />
                    {errors.ageCategory && formTouched.ageCategory && <div className="text-danger">{errors.ageCategory}</div>}
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-medium">
                      Team Name<span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <Form.Control 
                      name="name" 
                      value={form.name} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.name && formTouched.name} 
                      onBlur={() => setFormTouched({ ...formTouched, name: true })} 
                      readOnly={viewMode}
                      placeholder="Enter team name"
                    />
                    {errors.name && formTouched.name && <div className="text-danger">{errors.name}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-medium">
                      Team Abbreviation<span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <Form.Control 
                      name="shortName" 
                      value={form.shortName} 
                      onChange={(e) => {
                        // Only allow alphabets and limit to 6 characters
                        const value = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 6);
                        handleInputChange({ target: { name: 'shortName', value } } as any);
                      }}
                      isInvalid={!!errors.shortName && formTouched.shortName} 
                      onBlur={() => setFormTouched({ ...formTouched, shortName: true })} 
                      maxLength={6}
                      readOnly={viewMode}
                      placeholder="Enter abbreviation (max 6 letters)"
                    />
                    {errors.shortName && formTouched.shortName && <div className="text-danger">{errors.shortName}</div>}
                  </Form.Group>
                </div>
              </div>
            </Form>
          </Card.Body>
          <Card.Footer className="d-flex justify-content-end gap-2">
            {viewMode ? (
              <>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="primary" onClick={() => setViewMode(false)}>
                  <i className="ri-pencil-line me-2"></i>
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={!isFormValid || saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </>
            )}
          </Card.Footer>
        </Card>
      )}
      
        <div className="custom-table-responsive">
          <Table className="custom-table table-hover">
            <thead>
              <tr>
                <th>Logo</th>
                <th>Name</th>
                <th>Abbreviation</th>
                <th>Team ID</th>
                <th>Age Category</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamsLoading ? (
                <tr>
                  <td colSpan={6}><ShimmerLoader type="table" rows={5} /></td>
                </tr>
              ) : filteredTeams.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    {seasonNumber && seasonNumber > 1 && totalCount === 0 && !search && (!ageCategoryFilter || ageCategoryFilter.length === 0) ? (
                      <div className="text-center py-5">
                        <div className="empty-state">
                          <div className="empty-state-icon" style={{ marginBottom: '1rem' }}>
                            <img 
                              src={noResultFoundIcon} 
                              alt="Add Teams" 
                              style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                            />
                          </div>
                          <div className="empty-state-title" style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#333' }}>
                            Add Teams
                          </div>
                          <div className="empty-state-description" style={{ fontSize: '0.95rem', color: '#6c757d', marginBottom: '2rem' }}>
                            Add a new team to this season or select from existing teams already created.
                          </div>
                          <div className="d-flex justify-content-center gap-3">
                            <Button 
                              variant="outline-primary" 
                              onClick={handleOpenExistingTeamModal}
                              style={{ 
                                borderRadius: '6px', 
                                padding: '10px 20px', 
                                fontSize: '14px', 
                                fontWeight: '500',
                                backgroundColor: '#ffffff',
                                borderColor: '#0d6efd',
                                color: '#0d6efd',
                                borderWidth: '1px',
                                borderStyle: 'solid'
                              }}
                            >
                              Add Existing Team <span style={{ marginLeft: '4px' }}>+</span>
                            </Button>
                            <Button 
                              variant="primary" 
                              onClick={handleAdd}
                              style={{ 
                                borderRadius: '6px', 
                                padding: '10px 20px', 
                                fontSize: '14px', 
                                fontWeight: '500',
                                backgroundColor: '#0d6efd',
                                borderColor: '#0d6efd',
                                color: '#ffffff'
                              }}
                            >
                              Add New Team <span style={{ marginLeft: '4px' }}>+</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div className="empty-state">
                          <div className="empty-state-icon">
                            <i className="ri-team-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                          </div>
                          <div className="empty-state-title">No Teams Found</div>
                          <div className="empty-state-description">
                            {search || (ageCategoryFilter && ageCategoryFilter.length > 0)
                              ? 'No teams match your search or filter criteria.'
                              : 'There are no teams available for this tournament.'}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filteredTeams.map((team, idx) => {
                  // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
                  // team object contains the actual team data (name, logo, etc.)
                  const teamData = team.team || team; // Use nested team object if available, fallback to team itself
                  const teamSeasonId = team._id; // _id is now teamSeason id
                  return (
                    <tr key={teamSeasonId || teamData._id || teamData.id}>
                      <td>
                        <div className="team-logo-container">
                          {teamData.logo ? (
                            <img 
                              src={getImageUrl(teamData.logo)}
                              alt="Team Logo" 
                              className="team-logo-image"
                            />
                          ) : (
                            <div className="team-logo-placeholder">
                              <i className="ri-team-line"></i>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{teamData.name}</td>
                      <td>{teamData.shortName}</td>
                      <td>{getTeamId(team)}</td>
                      <td>{(() => {
                        // Use ageCategory from response (array of age category names)
                        const ageCategories = team.ageCategory || team.teamSeasonInfo?.ageCategory || [];
                        return Array.isArray(ageCategories) ? ageCategories.join(', ') : ageCategories;
                      })()}</td>
                      <td>
                        <div className="tournament-actions">
                          <button
                            className="action-btn btn-view"
                            onClick={() => handleView(idx)}
                            title="View Team"
                            type="button"
                            disabled={loadingTeamDetails}
                          >
                            {loadingTeamDetails ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-eye-line"></i>}
                          </button>
                          <button
                            className="action-btn btn-edit"
                            onClick={() => handleEdit(idx)}
                            title="Edit Team"
                            type="button"
                            disabled={loadingTeamDetails}
                          >
                            {loadingTeamDetails ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-pencil-line"></i>}
                          </button>
                          <button
                            className="action-btn btn-delete"
                            onClick={() => handleDelete(idx)}
                            title="Delete Team"
                            type="button"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

      {totalPages > 1 && (
        <div className="pagination-section mt-4" style={{ 
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="page-info mb-3">
            <span className="text-muted" style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#6c757d'
            }}>
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} teams
            </span>
          </div>

          <div className="pagination-controls d-flex align-items-center">
                <div className="page-numbers d-flex align-items-center">
            <Button
                    variant="outline-secondary"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
                    style={{ marginRight: '8px' }}
            >
              Previous
            </Button>
            
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else {
                      const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                      pageNum = startPage + i;
                    }
                    
                return (
                  <Button
                    key={pageNum}
                        variant={pageNum === page ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                        style={{ marginRight: '4px', minWidth: '32px' }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            
            <Button
                    variant="outline-secondary"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
                    style={{ marginLeft: '8px' }}
            >
              Next
            </Button>
        </div>

                <div className="page-size-selector ms-4">
                  <Form.Select
                    size="sm"
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    style={{ minWidth: '80px' }}
                  >
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </Form.Select>
                      </div>
                        </div>
                      </div>
                    )}
                
        <DeleteModal
          show={showDeleteModal}
          handleClose={closeDeleteModal}
          deleteModalFunction={confirmDelete}
          title="Delete Team"
          message={`Are you sure you want to delete '${deleteIndex !== null ? teams[deleteIndex].name : ''}'?`}
        />

        <Modal
          show={showExistingTeamModal}
          onHide={handleCloseExistingTeamModal}
          size="xl"
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Existing Teams</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '1.5rem', paddingBottom: '1rem', display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
            {existingTeamError && (
              <div className="alert alert-danger mb-3">{existingTeamError}</div>
            )}

            <div className="mb-4">
              <label className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>
                Age Category <span className="text-danger">*</span>
              </label>
              <Select
                value={selectedAgeCategory}
                onChange={handleExistingTeamAgeCategoryChange}
                options={ageCategoryOptions}
                placeholder="Select Age Category"
                isClearable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '38px',
                    borderRadius: '6px'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 99999
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999
                  })
                }}
              />
            </div>

            {selectedAgeCategory && (
              <>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Team Listing</h6>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '300px' }}>
                      <Form.Control
                        type="text"
                        placeholder="Search Teams"
                        value={teamSearchTerm}
                        onChange={(e) => setTeamSearchTerm(e.target.value)}
                        style={{ borderRadius: '6px' }}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        style={{ borderRadius: '6px', minWidth: '40px', padding: '6px' }}
                      >
                        <i className="ri-search-line" style={{ fontSize: '16px' }}></i>
                      </Button>
                    </div>
                  </div>
                </div>

                {(loadingPreviousTeams || loadingCurrentTeams) ? (
                  <div className="text-center py-5">
                    <ShimmerLoader type="table" rows={5} />
                  </div>
                ) : (
                  <div className="custom-table-responsive" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    <Table className="custom-table table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: '50px', textAlign: 'center' }}>
                            <Form.Check
                              type="checkbox"
                              checked={
                                (() => {
                                  const filteredTeams = getFilteredPreviousSeasonTeams();
                                  const selectableTeams = filteredTeams.filter((team: any) => {
                                    const teamId = team.team?._id || team._id;
                                    return !alreadyAddedTeamIds.has(teamId);
                                  });
                                  return selectableTeams.length > 0 &&
                                    selectableTeams.every((team: any) =>
                                      selectedTeamIds.has(team.team?._id || team._id)
                                    );
                                })()
                              }
                              onChange={handleSelectAllTeams}
                              style={{
                                cursor: 'pointer'
                              }}
                              className="custom-checkbox-select"
                            />
                          </th>
                          <th style={{ textAlign: 'left' }}>Logo</th>
                          <th style={{ textAlign: 'left' }}>Team Name</th>
                          <th style={{ textAlign: 'left' }}>Short Name</th>
                          <th style={{ textAlign: 'left' }}>Sports</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredPreviousSeasonTeams().length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center py-5">
                              <div className="empty-state">
                                <div className="empty-state-icon" style={{ marginBottom: '1rem' }}>
                                  <img 
                                    src={noResultFoundIcon} 
                                    alt="No teams found" 
                                    style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                                  />
                                </div>
                                <div className="empty-state-title" style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: '#333' }}>
                                  No teams found
                                </div>
                                <div className="empty-state-description" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                  No teams match your search criteria.
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          getFilteredPreviousSeasonTeams().map((team: any) => {
                            const teamData = team.team || team;
                            const teamId = team.team?._id || team._id || teamData._id;
                            const isAlreadyAdded = alreadyAddedTeamIds.has(teamId);
                            const isSelected = isAlreadyAdded ? true : selectedTeamIds.has(teamId);
                            
                            return (
                              <tr 
                                key={teamId}
                                style={{
                                  opacity: isAlreadyAdded ? 0.7 : 1
                                }}
                              >
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleTeamToggle(teamId)}
                                    disabled={isAlreadyAdded}
                                    style={{
                                      cursor: isAlreadyAdded ? 'not-allowed' : 'pointer'
                                    }}
                                    className="custom-checkbox-select"
                                  />
                                </td>
                                <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                                  {teamData.logo ? (
                                    <img
                                      src={getImageUrl(teamData.logo)}
                                      alt="Team Logo"
                                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                    />
                                  ) : (
                                    <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                                      <i className="ri-team-line"></i>
                                    </div>
                                  )}
                                </td>
                                <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{teamData.name || '-'}</td>
                                <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{teamData.shortName || '-'}</td>
                                <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{teamData.sports || 'Football'}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{ 
            position: 'sticky', 
            bottom: 0, 
            backgroundColor: '#fff', 
            borderTop: '1px solid #dee2e6',
            zIndex: 10,
            padding: '1rem 1.5rem',
            marginTop: 'auto',
            flexShrink: 0
          }}>
            <Button variant="secondary" onClick={handleCloseExistingTeamModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleMapTeams}
              disabled={!selectedAgeCategory || selectedTeamIds.size === 0 || mappingTeams}
            >
              {mappingTeams ? (
                <>
                  <i className="ri-loader-4-line animate-spin me-2"></i>
                  Adding Teams...
                </>
              ) : selectedTeamIds.size > 0 ? (
                'Add Teams'
              ) : (
                'Select Teams'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
        
        <TournamentBulkUploadModal
          show={showBulkUploadModal}
          onHide={() => setShowBulkUploadModal(false)}
          onSuccess={onAdd}
          resource="teams"
          tournamentId={tournamentId}
          tournamentSeasonId={tournamentSeasonId}
          sport={tournamentSports}
        />

        <style>{`
          .custom-checkbox-select .form-check-input {
            width: 20px;
            height: 20px;
            cursor: pointer;
            border: 1px solid #dee2e6;
            border-radius: 4px;
          }
          
          .custom-checkbox-select .form-check-input:checked {
            background-color: #ff6b35;
            border-color: #ff6b35;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
          }
          
          .custom-checkbox-select .form-check-input:focus {
            border-color: #ff6b35;
            box-shadow: 0 0 0 0.25rem rgba(255, 107, 53, 0.25);
          }
          
          .custom-checkbox-select .form-check-input:disabled {
            background-color: #28a745;
            border-color: #28a745;
            cursor: not-allowed;
            opacity: 0.8;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
          }
          
          .custom-checkbox-select .form-check-input:disabled:checked {
            background-color: #28a745;
            border-color: #28a745;
            opacity: 0.8;
          }
        `}</style>
    </div>
  );
};

export default TeamsTable; 